import { startTransition, type ReactNode, useEffect, useState } from 'react';

import type { AdminWorkspacesResponse } from '@/shared/shell-api-types';
import { shellApi } from '../api/shellApi';
import { WorkspaceContext } from '../catedge';

const emptyState: AdminWorkspacesResponse = {
  workspaces: [],
  activeOrganizationId: null,
};

export function AdminWorkspaceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminWorkspacesResponse>(emptyState);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void shellApi
      .listAdminWorkspaces()
      .then((nextState) => {
        if (cancelled) return;
        startTransition(() => {
          setState(nextState);
          setLoading(false);
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const refreshWorkspaces = async () => {
    setLoading(true);
    const nextState = await shellApi.listAdminWorkspaces();
    startTransition(() => {
      setState(nextState);
      setLoading(false);
    });
  };

  const switchWorkspace = async (organizationId: string) => {
    setLoading(true);
    try {
      const nextState = await shellApi.setActiveAdminWorkspace({ organizationId });
      startTransition(() => {
        setState(nextState);
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const activeOrganization =
    state.workspaces.find((workspace) => workspace.id === state.activeOrganizationId) ?? null;

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces: state.workspaces,
        activeOrganization,
        activeOrganizationId: state.activeOrganizationId,
        loading,
        switchWorkspace,
        refreshWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}
